// @flow

import * as React from 'react';
import {useContext, useMemo} from 'react';
import {useAccount, useCookies, useNavigation} from 'docs/data';
import {
  contextStubFunction,
  extractLinkAction,
  isBrowser,
  getSearchResultBreadcrumbs,
} from 'docs/util';
import type {Article, AnalyticsView, ViewHistoryEntry} from 'docs/data';
import {useGoogleTagManagerClient} from './useGoogleTagManagerClient';
import {useStripeAnalyticsClient} from './useStripeAnalyticsClient';

const VIEW_HISTORY_COOKIE = 'recent-views';
const VIEW_HISTORY_MAX_ITEMS = 4;

export type AddToViewHistoryFunction = (article: Article) => void;
export type GetViewHistoryFunction = () => Array<ViewHistoryEntry>;
export type TrackActionFunction = (name: string, properties: Object) => Object;
export type TrackViewFunction = (view: AnalyticsView) => Object;
export type TrackLinkClickFunction = (anchor: HTMLAnchorElement) => Object;

export type AnalyticsContextProps = {
  addToViewHistory: AddToViewHistoryFunction,
  getViewHistory: GetViewHistoryFunction,
  trackAction: TrackActionFunction,
  trackView: TrackViewFunction,
  trackLinkClick: TrackLinkClickFunction,
};

const AnalyticsContext = React.createContext<AnalyticsContextProps>({
  addToViewHistory: contextStubFunction('Analytics'),
  getViewHistory: contextStubFunction('Analytics'),
  trackAction: contextStubFunction('Analytics'),
  trackView: contextStubFunction('Analytics'),
  trackLinkClick: contextStubFunction('Analytics'),
});

type AnalyticsProviderProps = {|
  children: React.Node,
  requestId: string,
|};

export const AnalyticsProvider = (props: AnalyticsProviderProps) => {
  const {account} = useAccount();
  const {cookies, getCookieDomain} = useCookies();
  const {navigation} = useNavigation();

  const googleTagManager = useGoogleTagManagerClient();
  const stripeAnalytics = useStripeAnalyticsClient(account);

  const commonProperties = useMemo(
    () => ({
      country: account.country.code,
      // TODO (nkohari): Language and locale are duplicated for historic reasons.
      // It'd be great to remove this at some point if possible!
      language: account.locale.code,
      locale: account.locale.code,
      flags: account.flags,
      treatments: account.treatments,
      pageId: props.requestId,
    }),
    [account, props.requestId],
  );

  const getViewHistory = (): Array<ViewHistoryEntry> => {
    const value = cookies.get(VIEW_HISTORY_COOKIE);

    if (value) {
      try {
        return JSON.parse(value);
      } catch (err) {
        // If we fail to deserialize, pretend as though the cookie doesn't exist.
      }
    }

    return [];
  };

  const addToViewHistory = (article: Article) => {
    let entries = getViewHistory();

    // If the article is already present in the history, remove it.
    // Duplicate titles are an edge case condition for the integration builder (docs/checkout/integration-builder)
    entries = entries.filter(
      (entry) =>
        entry.url !== article.path &&
        entry.title !== article.metadata.title &&
        entry.url !== '/docs',
    );

    if (article.path === '/docs') {
      // Do not add the docs home page as a 'recently viewed' doc
      return;
    }

    // Add a new entry for the article to the end of the collection.
    entries.unshift({
      url: article.path,
      title: article.metadata.title,
      breadcrumbs: getSearchResultBreadcrumbs(navigation, article),
      timestamp: Date.now(),
    });

    // Ensure we don't exceed the cap on the collection.
    entries = entries.slice(0, VIEW_HISTORY_MAX_ITEMS);

    // This cookie is used for both the dashboard and the 'Recently viewed'
    // section on the search bar.
    // TODO: Consider moving this out of a cookie.
    cookies.set(VIEW_HISTORY_COOKIE, JSON.stringify(entries), {
      domain: getCookieDomain(),
    });
  };

  const value = {
    addToViewHistory,
    getViewHistory,
    trackAction: (name, properties) => {
      return stripeAnalytics.action(name, {
        url: document.location.pathname,
        ...properties,
        ...commonProperties,
      });
    },
    trackView: ({article, ...properties}: AnalyticsView) => {
      let referrer = '';
      let docsHistory = '';

      if (isBrowser) {
        referrer = document.referrer;
        const viewHistory = getViewHistory();
        docsHistory = viewHistory.length >= 2 && viewHistory[1].url;
      }

      googleTagManager.push({
        event: 'virtual-pageview',
        url: article.path,
        title: article.metadata.title,
        referrer,
      });

      // $FlowFixMe
      return stripeAnalytics.viewed('docs', {
        title: article.metadata.title,
        path: article.path,
        referrer,
        docsHistory,
        ...commonProperties,
        ...properties,
      });
    },
    trackLinkClick: (anchor: HTMLAnchorElement) => {
      const href = anchor.getAttribute('href');
      if (!href) {
        return;
      }
      const action = extractLinkAction(anchor);
      const params = {
        href,
        text: (anchor.innerText || '').trim().replace(/\s+/g, ' '),
        referrer: isBrowser ? document.referrer : '',
        classname: anchor.className,
      };
      return stripeAnalytics.action(action, {
        ...params,
        ...commonProperties,
      });
    },
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {props.children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  return useContext(AnalyticsContext);
};
